import type {
  AdminPaymentlineView,
  PaymentlineAuditView,
} from '../../services/admin-payments/admin-payments.service';
import type { AdminApprovalRequestView } from '../../services/admin-approval-requests/admin-approval-requests.service';
import type { AdminSessionAuditView, AdminSessionsSortKey } from '../../services/admin-sessions/admin-sessions.service';
import type { AdminKdsAuditSortKey, AdminKdsAuditView } from '../../services/admin-kds-audit/admin-kds-audit.service';

export const PAGE_SIZE = 100;

export type AmountFilterMode = 'all' | 'exact' | 'between';
export type DateFilterMode = 'all' | 'exact' | 'between';
export type SortDirection = 'asc' | 'desc';
export type SortKey =
  | 'companyName'
  | 'sessionName'
  | 'orderCode'
  | 'tableNumber'
  | 'status'
  | 'method'
  | 'amount'
  | 'orderStatus'
  | 'createdAt'
  | 'updatedAt'
  | 'canceledAt';

export const AMOUNT_FILTER_OPTIONS = [
  { label: 'כל הסכומים', value: 'all' },
  { label: 'סכום מדויק', value: 'exact' },
  { label: 'בין סכומים', value: 'between' },
];

export const DATE_FILTER_OPTIONS = [
  { label: 'כל התאריכים', value: 'all' },
  { label: 'תאריך מדויק', value: 'exact' },
  { label: 'בין תאריכים', value: 'between' },
];

export const METHOD_LABELS: Record<AdminPaymentlineView['method'], string> = {
  cash: 'מזומן',
  credit: 'אשראי',
};

export const STATUS_LABELS: Record<AdminPaymentlineView['status'], string> = {
  new: 'חדש',
  done: 'הושלם',
  canceled_split: 'בוטל לפיצול',
  canceled_reversed: 'הועבר',
  canceled_reset: 'התאפס',
};

export const ORDER_STATUS_LABELS: Record<AdminPaymentlineView['orderStatus'], string> = {
  open: 'פתוחה',
  closed: 'סגורה',
};

export const SORT_LABELS: Record<SortKey, string> = {
  companyName: 'חברה',
  sessionName: 'משמרת',
  orderCode: 'הזמנה',
  tableNumber: 'שולחן',
  status: 'סטטוס תשלום',
  method: 'אופן תשלום',
  amount: 'סכום',
  orderStatus: 'סטטוס הזמנה',
  createdAt: 'נוצר ב',
  updatedAt: 'שונה ב',
  canceledAt: 'שונה ב',
};

export const SESSION_STATE_LABELS: Record<AdminSessionAuditView['state'], string> = {
  open: 'פתוחה',
  closed: 'סגורה',
};

export const SESSION_SORT_LABELS: Record<AdminSessionsSortKey, string> = {
  companyName: 'חברה',
  sessionName: 'משמרת',
  openingUser: 'נפתחה ע״י',
  openTime: 'נפתחה',
  closeTime: 'נסגרה',
  state: 'סטטוס',
  orderCount: 'מספר הזמנות',
  salesTotal: 'סה״כ מכירות',
  cashTotal: 'מזומן',
  creditTotal: 'אשראי',
};

export const APPROVAL_ACTION_LABELS: Record<AdminApprovalRequestView['actionType'], string> = {
  global_discount: 'הנחה כללית',
  item_discount: 'הנחת פריט',
  cancel: 'ביטול',
  on_the_house: 'על חשבון הבית',
  general_item: 'פריט כללי',
};

export const getApprovalActionLabel = (request: Pick<AdminApprovalRequestView, 'actionType' | 'payload'>): string => {
  if (request.payload.actionType === 'cancel' && request.payload.cancelScope === 'order') {
    return 'ביטול כל ההזמנה';
  }
  return APPROVAL_ACTION_LABELS[request.actionType];
};

export type ApprovalActionTone = 'globalDiscount' | 'itemDiscount' | 'cancel' | 'onTheHouse' | 'generalItem';

export const getApprovalActionTone = (request: Pick<AdminApprovalRequestView, 'actionType'>): ApprovalActionTone => {
  switch (request.actionType) {
    case 'global_discount':
      return 'globalDiscount';
    case 'item_discount':
      return 'itemDiscount';
    case 'cancel':
      return 'cancel';
    case 'on_the_house':
      return 'onTheHouse';
    case 'general_item':
      return 'generalItem';
  }
};

export const APPROVAL_REASON_LABELS: Record<AdminApprovalRequestView['reason'], string> = {
  cancel_order: 'ביטול הזמנה',
  vip: 'VIP',
  waiter_error: 'טעות מלצר',
  staff: 'צוות',
  other: 'אחר',
  manager_action: 'פעולת מנהל',
};

export const APPROVAL_STATUS_LABELS: Record<AdminApprovalRequestView['status'], string> = {
  pending: 'ממתינה',
  approved: 'אושרה',
  denied: 'נדחתה',
};

export const KITCHEN_STATUS_LABELS: Record<NonNullable<AdminKdsAuditView['kitchenStatus']>, string> = {
  COOKING: 'בהכנה',
  READY: 'מוכן',
  DONE: 'הוגש',
  HIDE: 'מוסתר',
};

export const ORDER_TYPE_LABELS: Record<AdminKdsAuditView['orderType'], string> = {
  dine_in: 'בישיבה',
  take_away: 'לקחת',
};

export const KDS_AUDIT_ORDER_STATUS_LABELS: Record<AdminKdsAuditView['orderStatus'], string> = {
  open: 'פתוחה',
  closed: 'סגורה',
};

export const KDS_AUDIT_SORT_LABELS: Record<AdminKdsAuditSortKey, string> = {
  companyName: 'חברה',
  sessionName: 'משמרת',
  orderCode: 'הזמנה',
  tableName: 'שולחן',
  productName: 'מוצר',
  kitchenStatus: 'סטטוס',
  orderStatus: 'סטטוס הזמנה',
  createdByName: 'נוצר ע״י',
  modifiedByName: 'שונה ע״י',
};

export const LOG_PLACE_LABELS: Record<'backend' | 'frontend', string> = {
  backend: 'שרת',
  frontend: 'לקוח',
};

export const LOG_PLACE_OPTIONS = [
  { value: 'backend', label: 'שרת' },
  { value: 'frontend', label: 'לקוח' },
];

export const LOG_LEVEL_LABELS: Record<'error' | 'warn' | 'info' | 'debug', string> = {
  error: 'שגיאה',
  warn: 'אזהרה',
  info: 'מידע',
  debug: 'דיבאג',
};

export const STATUS_CLASS_OPTIONS = [
  { value: '4xx', label: '4xx' },
  { value: '5xx', label: '5xx' },
];

export const IGNORED_FILTER_OPTIONS = [
  { value: 'hide', label: 'הסתר מוסתרות' },
  { value: 'only', label: 'הצג רק מוסתרות' },
  { value: 'all', label: 'הצג הכל' },
];

// Amber for client-fault 4xx, red for server-fault 5xx; anything else has no status badge.
export const resolveStatusClass = (httpStatus: number | null | undefined): '4xx' | '5xx' | null => {
  if (httpStatus == null) return null;
  if (httpStatus >= 400 && httpStatus <= 499) return '4xx';
  if (httpStatus >= 500 && httpStatus <= 599) return '5xx';
  return null;
};

export const AUDIT_ACTION_LABELS: Record<PaymentlineAuditView['action'], string> = {
  created: 'נוצר',
  status_changed: 'סטטוס',
  method_changed: 'אופן תשלום',
  amount_changed: 'סכום',
  updated: 'עדכון',
  payment_canceled: 'תשלום בוטל',
  transferred: 'הועברה להזמנה אחרת',
  zcredit_reference_approved: 'אושר על ידי ז קרדיט על פי הרפרנס איידי',
  credit_response: 'תגובת אשראי',
};
